
import React, { useContext, useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { AppContext } from '../App';
import { supabase } from '../supabase';
import { Task, ChecklistItem } from '../types';

const KanbanCard: React.FC<{
  task: Task;
  onStatusChange: (id: string, newStatus: 'To Do' | 'Doing' | 'Done') => void;
  onEdit: (task: Task) => void;
  onDelete: (id: string) => void;
  onDragStart: (e: React.DragEvent, id: string) => void;
  onToggleCheck: (taskId: string, itemId: string) => void;
  projectName?: string;
}> = ({ task, onStatusChange, onEdit, onDelete, onDragStart, onToggleCheck, projectName }) => {
  const doneCount = task.checklist?.filter(i => i.done).length || 0;
  const totalCount = task.checklist?.length || 0;
  const progressPercent = totalCount > 0 ? (doneCount / totalCount) * 100 : 0;

  return (
    <div
      draggable="true"
      onDragStart={(e) => onDragStart(e, task.id)}
      className="bg-white p-5 rounded-[23px] shadow-sm border border-gray-100/50 hover:border-primary/20 hover:shadow-md hover:scale-[1.01] cursor-grab active:cursor-grabbing group transition-all duration-300 relative flex flex-col gap-4 snap-center mx-1"
    >
      <div className="flex justify-between items-start gap-2">
        <div className="flex flex-col gap-2 min-w-0">
          <span className="w-fit text-[9px] font-black px-3 py-1.5 rounded-full uppercase tracking-widest bg-primary/5 text-primary border border-primary/10 shadow-sm">
            {task.tag}
          </span>
          {projectName && (
            <span className="text-[10px] font-black text-primary/60 uppercase tracking-widest flex items-center gap-1.5 truncate">
              <span className="material-symbols-outlined text-[14px]">folder</span>
              {projectName}
            </span>
          )}
        </div>
        <div className="flex gap-1 shrink-0">
          <button onClick={(e) => { e.stopPropagation(); onEdit(task); }} className="size-9 flex items-center justify-center rounded-xl bg-gray-50 text-text-main border border-gray-100 active:scale-90 transition-all">
            <span className="material-symbols-outlined text-[18px]">edit</span>
          </button>
          <button onClick={(e) => { e.stopPropagation(); onDelete(task.id); }} className="size-9 flex items-center justify-center rounded-xl bg-red-50 text-red-600 border border-red-50 active:scale-90 transition-all">
            <span className="material-symbols-outlined text-[18px]">delete</span>
          </button>
        </div>
      </div>

      <h3 className="text-text-main font-black text-[16px] leading-tight transition-colors">
        {task.name}
      </h3>

      {totalCount > 0 && (
        <div className="space-y-3">
          <div className="flex flex-col gap-2 max-h-32 overflow-y-auto scrollbar-hide">
            {task.checklist.map(item => (
              <label
                key={item.id}
                className="flex items-center gap-3 cursor-pointer py-0.5 group/item"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="relative flex items-center justify-center">
                  <input
                    type="checkbox"
                    checked={item.done}
                    onChange={() => onToggleCheck(task.id, item.id)}
                    className="size-5 rounded-lg border-2 border-gray-200 text-primary focus:ring-primary transition-all cursor-pointer checked:border-primary"
                  />
                </div>
                <span className={`text-[12px] font-bold transition-all ${item.done ? 'text-gray-300 line-through' : 'text-text-secondary'}`}>
                  {item.text}
                </span>
              </label>
            ))}
          </div>

          <div className="space-y-1.5">
            <div className="flex justify-between items-end">
              <span className="text-[9px] font-black text-gray-400 uppercase tracking-[0.15em]">Progresso Metas</span>
              <span className="text-[10px] font-black text-primary">{Math.round(progressPercent)}%</span>
            </div>
            <div className="w-full bg-gray-50 h-2 rounded-full overflow-hidden border border-gray-100">
              <div
                className={`h-full transition-all duration-700 ease-out shadow-sm rounded-full ${progressPercent === 100 ? 'bg-gradient-to-r from-green-400 to-green-500' : 'bg-gradient-to-r from-primary to-rose-400'}`}
                style={{ width: `${progressPercent}%` }}
              ></div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-2 pt-4 border-t border-gray-50 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="size-8 rounded-full border-2 border-white shadow-sm overflow-hidden bg-gray-100">
              <img src={task.assigneeAvatar} className="w-full h-full object-cover" alt="" />
            </div>
            <span className="text-[9px] font-black text-text-secondary uppercase tracking-widest">Responsável</span>
          </div>
          <div className={`px-3 py-1 rounded-full text-[10px] font-black tracking-tight ${progressPercent === 100 ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-text-main'}`}>
            {doneCount}/{totalCount} <span className="text-[8px] opacity-40 ml-0.5">ITEMS</span>
          </div>
        </div>

        <div className="flex gap-2">
          {task.status === 'To Do' && (
            <button
              onClick={() => onStatusChange(task.id, 'Doing')}
              className="flex-1 h-11 bg-primary-light text-primary rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-primary hover:text-white transition-all active:scale-95"
            >
              <span className="material-symbols-outlined text-[16px]">play_arrow</span> Iniciar
            </button>
          )}
          {task.status === 'Doing' && (
            <>
              <button
                onClick={() => onStatusChange(task.id, 'To Do')}
                className="size-11 bg-gray-50 text-text-secondary rounded-xl flex items-center justify-center hover:bg-gray-100 transition-all active:scale-95"
              >
                <span className="material-symbols-outlined text-[20px]">undo</span>
              </button>
              <button
                onClick={() => onStatusChange(task.id, 'Done')}
                className="flex-1 h-11 bg-green-50 text-green-600 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-green-600 hover:text-white transition-all active:scale-95"
              >
                <span className="material-symbols-outlined text-[16px]">check</span> Finalizar
              </button>
            </>
          )}
          {task.status === 'Done' && (
            <button
              onClick={() => onStatusChange(task.id, 'Doing')}
              className="flex-1 h-11 bg-gray-50 text-text-secondary rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-primary-light hover:text-primary transition-all active:scale-95"
            >
              <span className="material-symbols-outlined text-[16px]">settings_backup_restore</span> Reabrir
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const Kanban: React.FC = () => {
  const context = useContext(AppContext);
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [targetStatus, setTargetStatus] = useState<'To Do' | 'Doing' | 'Done'>('To Do');
  const [activeMobileTab, setActiveMobileTab] = useState<'To Do' | 'Doing' | 'Done'>('To Do');
  const [modalChecklist, setModalChecklist] = useState<ChecklistItem[]>([]);
  const [newItemText, setNewItemText] = useState('');

  if (!context) return null;
  const { tasks, searchTerm, projects } = context;

  const handleStatusChange = async (taskId: string, newStatus: 'To Do' | 'Doing' | 'Done') => {
    const { error } = await supabase.from('tasks').update({ status: newStatus }).eq('id', taskId);
    if (error) alert(error.message);
    if (window.innerWidth < 768) setActiveMobileTab(newStatus);
  };

  const handleToggleCheck = async (taskId: string, itemId: string) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;
    const newChecklist = task.checklist.map(i => i.id === itemId ? { ...i, done: !i.done } : i);
    const { error } = await supabase.from('tasks').update({ checklist: newChecklist }).eq('id', taskId);
    if (error) alert(error.message);
  };

  const handleSaveTask = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const projectId = formData.get('projectId') as string;
    const selectedProjectId = projectId === "none" ? null : projectId;
    let resolvedProjectId: string | null = null;
    if (selectedProjectId) {
      const { data, error } = await supabase
        .from('projects')
        .select('id')
        .eq('id', selectedProjectId)
        .maybeSingle();
      if (error) {
        alert('Erro ao validar projeto. Tente novamente.');
        return;
      }
      if (!data?.id) {
        alert('Projeto não encontrado no banco. Recarregue a página e tente de novo.');
        return;
      }
      resolvedProjectId = selectedProjectId;
    }
    const taskData = {
      name: formData.get('name') as string,
      tag: (formData.get('tag') as string || 'Geral').toUpperCase(),
      project_id: resolvedProjectId,
      checklist: modalChecklist
    };

    if (editingTask) {
      const { error } = await supabase.from('tasks').update(taskData).eq('id', editingTask.id);
      if (error) alert(error.message);
    } else {
      const { error } = await supabase.from('tasks').insert([{
        id: `T${Date.now()}`,
        status: targetStatus,
        assignee_avatar: `https://ui-avatars.com/api/?name=Admin&background=8a0049&color=fff&rounded=true`,
        ...taskData
      }]);
      if (error) alert(error.message);
    }
    closeModal();
  };

  const deleteTask = async (id: string) => {
    if (confirm('Excluir esta tarefa?')) {
      const { error } = await supabase.from('tasks').delete().eq('id', id);
      if (error) alert(error.message);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingTask(null);
    setModalChecklist([]);
  };

  const renderColumn = (status: 'To Do' | 'Doing' | 'Done', title: string) => {
    const colTasks = tasks.filter(t => t.status === status && t.name.toLowerCase().includes(searchTerm.toLowerCase()));
    return (
      <div className={`w-full md:w-[360px] flex flex-col h-full shrink-0 snap-center ${activeMobileTab === status ? 'flex' : 'hidden md:flex'}`}>
        <div className="flex items-center justify-between mb-4 px-4 pt-4">
          <div className="flex items-center gap-3">
            <h2 className="text-text-main font-black text-2xl tracking-tighter">{title}</h2>
            <span className="px-2.5 py-1 rounded-[10px] bg-gray-100 text-text-secondary text-xs font-black">{colTasks.length}</span>
          </div>
          <button onClick={() => { setTargetStatus(status); setIsModalOpen(true); }} className="size-10 flex items-center justify-center rounded-2xl bg-primary text-white shadow-lg shadow-primary/20 hover:scale-105 hover:shadow-primary/40 transition-all">
            <span className="material-symbols-outlined font-black">add</span>
          </button>
        </div>

        <div className="flex-1 bg-gray-50/80 rounded-[23px] p-3 md:p-4 flex flex-col gap-4 overflow-y-auto scrollbar-hide mx-2 mb-2 border border-gray-100">
          {colTasks.length > 0 ? (
            colTasks.map(t => (
              <KanbanCard
                key={t.id}
                task={t}
                onStatusChange={handleStatusChange}
                onEdit={(tk) => { setEditingTask(tk); setModalChecklist(tk.checklist); setIsModalOpen(true); }}
                onDelete={deleteTask}
                onDragStart={() => { }}
                onToggleCheck={handleToggleCheck}
                projectName={projects.find(p => p.id === t.projectId)?.name}
              />
            ))
          ) : (
            <div className="h-32 flex flex-col items-center justify-center text-center opacity-40 border-2 border-dashed border-gray-200 rounded-[23px]">
              <span className="material-symbols-outlined text-3xl mb-1">splitscreen_bottom</span>
              <p className="text-[10px] font-black uppercase tracking-widest">Sem tarefas</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full bg-background-light overflow-hidden">
      <div className="p-6 md:p-8 pb-4 shrink-0">
        <h2 className="text-3xl font-black text-text-main tracking-tight">Fluxo de Trabalho</h2>
        <p className="text-sm text-text-secondary font-bold mt-1">Sincronizado com Supabase Realtime.</p>
      </div>

      <div className="md:hidden px-6 pb-4 flex gap-2 overflow-x-auto scrollbar-hide">
        {(['To Do', 'Doing', 'Done'] as const).map(tab => (
          <button key={tab} onClick={() => setActiveMobileTab(tab)} className={`flex-1 py-3 rounded-[18px] text-[10px] font-black uppercase tracking-widest ${activeMobileTab === tab ? 'bg-primary text-white shadow-md' : 'bg-gray-100 text-text-secondary'}`}>
            {tab === 'To Do' ? 'A Fazer' : tab === 'Doing' ? 'Em Curso' : 'Feito'}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-x-auto md:px-6 pb-6 scrollbar-hide">
        <div className="flex h-full gap-4 md:gap-2 min-w-full md:min-w-0 md:justify-start snap-x snap-mandatory">
          {renderColumn('To Do', 'A Fazer')}
          {renderColumn('Doing', 'Em Curso')}
          {renderColumn('Done', 'Concluído')}
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-text-main/60 backdrop-blur-md animate-in fade-in duration-300">
          <div className="bg-white w-full max-w-lg rounded-[23px] shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="p-8 border-b border-gray-50 flex items-center justify-between">
              <h3 className="text-2xl font-black text-text-main tracking-tight">{editingTask ? 'Editar Tarefa' : 'Nova Tarefa'}</h3>
              <button onClick={closeModal} className="size-12 flex items-center justify-center rounded-full hover:bg-gray-100"><span className="material-symbols-outlined">close</span></button>
            </div>
            <form onSubmit={handleSaveTask} className="p-8 overflow-y-auto flex flex-col gap-6">
              <div className="flex flex-col gap-2">
                <label className="text-[10px] font-black uppercase text-text-secondary">Projeto</label>
                <select name="projectId" defaultValue={editingTask?.projectId || "none"} className="h-14 px-6 rounded-2xl bg-gray-50 border-2 border-gray-200 outline-none font-bold text-sm focus:border-primary/20 transition-all">
                  <option value="none">Nenhum</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-[10px] font-black uppercase text-text-secondary">Título</label>
                <input name="name" defaultValue={editingTask?.name} required className="h-14 px-6 rounded-2xl bg-gray-50 border-2 border-gray-200 outline-none font-bold text-sm focus:border-primary/20 transition-all" />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-[10px] font-black uppercase text-text-secondary">Tag</label>
                <input name="tag" defaultValue={editingTask?.tag} className="h-14 px-6 rounded-2xl bg-gray-50 border-2 border-gray-200 outline-none font-bold text-sm focus:border-primary/20 transition-all" />
              </div>

              <div className="space-y-4">
                <label className="text-[10px] font-black uppercase text-text-secondary">Checklist</label>
                <div className="flex gap-2">
                  <input value={newItemText} onChange={e => setNewItemText(e.target.value)} className="flex-1 h-12 px-5 rounded-2xl bg-gray-50 border-2 border-gray-200 outline-none text-sm font-bold focus:border-primary/20 transition-all" placeholder="Novo item..." />
                  <button type="button" onClick={() => { if (newItemText.trim()) { setModalChecklist([...modalChecklist, { id: Date.now().toString(), text: newItemText, done: false }]); setNewItemText(''); } }} className="size-12 rounded-2xl bg-primary text-white flex items-center justify-center"><span className="material-symbols-outlined">add</span></button>
                </div>
                {modalChecklist.map(item => (
                  <div key={item.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl">
                    <span className="text-xs font-bold">{item.text}</span>
                    <button type="button" onClick={() => setModalChecklist(modalChecklist.filter(i => i.id !== item.id))} className="text-red-500"><span className="material-symbols-outlined text-[18px]">delete</span></button>
                  </div>
                ))}
              </div>
              <button type="submit" className="h-16 rounded-2xl bg-primary text-white font-black uppercase tracking-widest shadow-lg">Salvar Tarefa</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Kanban;
